import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import { getOnboardingDraft, setOnboardingDraft } from '../core/storage/onboardingStorage';
import { createEmptyOnboardingDraft } from '../data/onboardingDefaults';
import {
  seedAssetsFromCatalog,
  seedLiabilitiesFromCatalog,
  getCurrencyForCountry,
  getIncomeMarks,
} from '../data/countryInvestmentCatalog';
import type { OnboardingCountryCode, OnboardingDraft } from '../types/onboarding';
import { ONBOARDING_LAST_STEP, ONBOARDING_STEP } from '../types/onboarding';
import { detectCountryFromLocale } from '../utils/detectLocaleCountry';

type OnboardingContextValue = {
  draft: OnboardingDraft;
  isHydrated: boolean;
  isModalVisible: boolean;
  needsSetup: boolean;
  browseSample: () => Promise<void>;
  startSetup: () => Promise<void>;
  reopen: (partial?: Partial<OnboardingDraft>) => Promise<void>;
  closeModal: () => void;
  updateDraft: (partial: Partial<OnboardingDraft>) => Promise<void>;
  setCountry: (countryCode: OnboardingCountryCode) => Promise<void>;
  setStep: (step: number) => Promise<void>;
  goToNextStep: () => Promise<void>;
  goToPrevStep: () => Promise<void>;
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: PropsWithChildren) {
  const [draft, setDraft] = useState<OnboardingDraft>(() =>
    createEmptyOnboardingDraft(detectCountryFromLocale()),
  );
  const [isHydrated, setIsHydrated] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    let mounted = true;
    getOnboardingDraft().then((stored) => {
      if (!mounted) return;
      setDraft(stored);
      // Never auto-open the first-launch / guest sheet. Guests see the sample
      // dashboard + SampleDataBanner; country/income/upload is post-login.
      setIsModalVisible(false);
      setIsHydrated(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const persist = useCallback(async (next: OnboardingDraft) => {
    setDraft(next);
    await setOnboardingDraft(next);
  }, []);

  const browseSample = useCallback(async () => {
    const next: OnboardingDraft = {
      ...draft,
      dismissedModal: true,
      step: draft.completedIntro ? draft.step : ONBOARDING_STEP.INTRO,
    };
    await persist(next);
    setIsModalVisible(false);
  }, [draft, persist]);

  const startSetup = useCallback(async () => {
    const next: OnboardingDraft = {
      ...draft,
      completedIntro: true,
      dismissedModal: false,
      step: ONBOARDING_STEP.COUNTRY_INCOME,
      assets: draft.assets.length > 0 ? draft.assets : seedAssetsFromCatalog(draft.countryCode),
      liabilities:
        draft.liabilities.length > 0 ? draft.liabilities : seedLiabilitiesFromCatalog(),
    };
    await persist(next);
    setIsModalVisible(true);
  }, [draft, persist]);

  const reopen = useCallback(
    async (partial?: Partial<OnboardingDraft>) => {
      const next: OnboardingDraft = {
        ...draft,
        ...partial,
        dismissedModal: false,
      };
      await persist(next);
      setIsModalVisible(true);
    },
    [draft, persist],
  );

  const closeModal = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const updateDraft = useCallback(
    async (partial: Partial<OnboardingDraft>) => {
      await persist({ ...draft, ...partial });
    },
    [draft, persist],
  );

  const setCountry = useCallback(
    async (countryCode: OnboardingCountryCode) => {
      const marks = getIncomeMarks(countryCode);
      const assets =
        draft.assets.length === 0 || draft.assets.every((a) => a.value === 0)
          ? seedAssetsFromCatalog(countryCode)
          : draft.assets;
      await persist({
        ...draft,
        countryCode,
        currencyCode: getCurrencyForCountry(countryCode),
        // Keep existing income if user already edited; otherwise use country mid.
        monthlyIncomeAfterTax:
          draft.monthlyIncomeAfterTax === getIncomeMarks(draft.countryCode).mid
            ? marks.mid
            : draft.monthlyIncomeAfterTax,
        assets,
      });
    },
    [draft, persist],
  );

  const setStep = useCallback(
    async (step: number) => {
      const clamped = Math.max(ONBOARDING_STEP.INTRO, Math.min(ONBOARDING_LAST_STEP, step));
      await persist({ ...draft, step: clamped });
    },
    [draft, persist],
  );

  const goToNextStep = useCallback(async () => {
    const nextStep = Math.min(ONBOARDING_LAST_STEP, draft.step + 1);
    let next: OnboardingDraft = { ...draft, step: nextStep, completedIntro: true };

    if (nextStep === ONBOARDING_STEP.ASSETS && next.assets.length === 0) {
      next = { ...next, assets: seedAssetsFromCatalog(next.countryCode) };
    }
    if (nextStep === ONBOARDING_STEP.LIABILITIES && next.liabilities.length === 0) {
      next = { ...next, liabilities: seedLiabilitiesFromCatalog() };
    }

    await persist(next);
  }, [draft, persist]);

  const goToPrevStep = useCallback(async () => {
    const prev = Math.max(ONBOARDING_STEP.INTRO, draft.step - 1);
    await persist({ ...draft, step: prev });
  }, [draft, persist]);

  /** Show Get started / Continue until the upload step has been reached and dismissed. */
  const needsSetup = !(
    draft.completedIntro &&
    draft.step >= ONBOARDING_LAST_STEP &&
    draft.dismissedModal
  );

  const value = useMemo<OnboardingContextValue>(
    () => ({
      draft,
      isHydrated,
      isModalVisible,
      needsSetup,
      browseSample,
      startSetup,
      reopen,
      closeModal,
      updateDraft,
      setCountry,
      setStep,
      goToNextStep,
      goToPrevStep,
    }),
    [
      draft,
      isHydrated,
      isModalVisible,
      needsSetup,
      browseSample,
      startSetup,
      reopen,
      closeModal,
      updateDraft,
      setCountry,
      setStep,
      goToNextStep,
      goToPrevStep,
    ],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return ctx;
}
